'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button } from '@extern/ui/button';
import { Checkbox } from '@extern/ui/checkbox';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@extern/ui/form';
import { Input } from '@extern/ui/input';
import { InputPassword, InputPasswordTooltip } from '@extern/ui/input-password';
import { LinkButton } from '@extern/ui/link-button';
import { Separator } from '@extern/ui/separator';
import { Text } from '@extern/ui/text';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { type Resolver, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { useAuthEvents } from '@/app/(auth)/_hooks/use-auth-events';
import { AuthGoogleButton } from '@/app/(auth)/_partials/auth-google-button';
import { AuthLastUsedButtonWrap } from '@/app/(auth)/_partials/auth-last-used-badge';
import { PORTFOLIO_LOCKED_EMAIL_INPUT_PROPS, PORTFOLIO_LOCKED_EMAIL_LABEL_PROPS, PortfolioLockedEmailHint } from '@/app/(auth)/_partials/portfolio-arrival/portfolio-locked-email';
import { PORTFOLIO_ARRIVAL_SUBMIT_LABEL, usePortfolioArrivalFields } from '@/app/(auth)/_partials/portfolio-arrival/use-portfolio-arrival-fields';
import { EMAIL_AUTHENTICATION_STRATEGIES } from '@/app/(auth)/_utils/auth-last-authentication-strategy';
import { prefilledEmail, prefilledNewsletter } from '@/app/(auth)/_utils/prefilled-email';
import { emailSchema, passwordSchema } from '@/infra/schemas/auth';
import { getAllowlistedSearchParams } from '@/utils/get-allowlisted-search-params';
import { useLastAuthenticationStrategy } from '../../_hooks/use-last-authentication-strategy';
import { usePasswordlessSignUpVariant } from '../../_hooks/use-passwordless-sign-up-variant';
import { useSignUp } from '../_hooks/use-sign-up';
import { useSignUpFlowStore } from '../_hooks/use-sign-up-flow-store';
const BaseFormSchema = z.object({
  firstName: z.string().min(2, {
    message: 'First name is required'
  }),
  lastName: z.string().min(2, {
    message: 'Last name is required'
  }),
  emailAddress: emailSchema
});
const FormSchema = BaseFormSchema.extend({
  password: passwordSchema
});
// The passwordless arm, until the reader asks for a password: the field is not on screen, so whatever
// it holds is neither validated nor submitted.
const PasswordlessFormSchema = BaseFormSchema.extend({
  password: z.string()
});
type FormValues = z.infer<typeof FormSchema>;
interface FormContext {
  requiresPassword: boolean;
}
const passwordResolver = zodResolver(FormSchema);
const passwordlessResolver = zodResolver(PasswordlessFormSchema);

// Which rules apply is read from the form's `context` at validation time, not fixed at mount: the arm
// can resolve after the form has mounted, and the reader can switch between the two ways in.
const resolver: Resolver<FormValues, FormContext> = (values, context, options) => (context?.requiresPassword === false ? passwordlessResolver : passwordResolver)(values, context, options);

/**
 * What the reader is looking at, for `auth_screen_viewed`.
 *
 * The treatment has one screen and reports itself as such; control keeps the two-screen names so its half
 * of the funnel stays readable against the weeks before this test.
 */
function signUpScreenName(isOneScreen: boolean, isFormVisible: boolean): string {
  if (isOneScreen) {
    return 'start_one_screen';
  }
  return isFormVisible ? 'start_email_form' : 'start';
}
function submitLabel(requiresPassword: boolean, isPortfolioArrival: boolean): string {
  if (!requiresPassword) {
    return 'Email me a code';
  }
  return isPortfolioArrival ? PORTFOLIO_ARRIVAL_SUBMIT_LABEL : 'Continue';
}
export function SignUpFormExperiment() {
  const searchParams = useSearchParams();
  const isFormVisible = useSignUpFlowStore(s => s.isStartFormVisible);
  const newsletter = useSignUpFlowStore(s_0 => s_0.newsletter);
  const startFormDraft = useSignUpFlowStore(s_1 => s_1.startFormDraft);
  const onChangeFlow = useSignUpFlowStore(s_2 => s_2.onChange);
  const {
    isPending,
    isGoogleLoading,
    onSignUpSubmit,
    onSignUpWithGoogle
  } = useSignUp();
  const {
    trackAuthScreenViewed,
    trackSignUpChoiceClicked,
    trackSignUpEmailReturnClicked,
    trackSignUpPasswordOptionToggled
  } = useAuthEvents();
  // ONE ARM, ONE SCREEN. The treatment is the whole new sign-up: no chooser, no password field, and the
  // password as an opt-in link. It shipped as two flags for a few days (`ab_test_one_screen_sign_up_2026_q3`,
  // never ramped above 0%); two flags gating one screen means a visitor sits in both and neither reads
  // clean, so the one screen now rides on the passwordless arm and that flag is archived.
  const {
    isPasswordless
  } = usePasswordlessSignUpVariant();
  const isOneScreen = isPasswordless;
  // Seeded from the draft so a reader who chose a password, went to the code screen and came back does
  // not find the field gone and their password silently dropped.
  const [wantsPassword, setWantsPassword] = useState(() => Boolean(startFormDraft.password));
  const requiresPassword = !isPasswordless || wantsPassword;
  const lastAuthenticationStrategy = useLastAuthenticationStrategy();
  const lastTrackedScreenRef = useRef<string | null>(null);
  const form = useForm<FormValues, FormContext>({
    resolver,
    context: {
      requiresPassword
    },
    // The draft wins when the reader has already typed something, so returning to the form never wipes
    // their work; the `?email=` prefill only fills a gap it would otherwise leave empty.
    defaultValues: {
      ...startFormDraft,
      emailAddress: startFormDraft.emailAddress || prefilledEmail(searchParams)
    },
    mode: 'onChange'
  });

  // Inert unless the visitor just claimed a portfolio (see `PortfolioArrivalLayout`).
  const portfolio = usePortfolioArrivalFields(form.setValue, form.getValues);

  // The arm can resolve after mount (PostHog answers late on a first visit). A reader who already typed
  // a password before it landed keeps the field: taking it away mid-keystroke would be worse than
  // showing them the form they started.
  useEffect(() => {
    if (isPasswordless && !wantsPassword && form.getValues('password')) {
      setWantsPassword(true);
    }
  }, [form, isPasswordless, wantsPassword]);

  // Computed from the live values rather than read from `formState.isValid`, which is only refreshed
  // when a field changes and so goes stale the moment the rules change underneath it.
  const watchedValues = useWatch({
    control: form.control
  });
  const canSubmit = (requiresPassword ? FormSchema : PasswordlessFormSchema).safeParse(watchedValues).success;

  // 🔴 A READER WHO ARRIVES WITH AN EMAIL SKIPS THE CHOOSER. The Labs preview hands people here having
  // already collected their address and told them there is "one step left". Landing them on a
  // Google-or-Email chooser that shows no sign of knowing them made the promise false and the site look
  // like it forgot them (Jay hit exactly this testing the flow). So the email form opens directly, with
  // their address filled in; Google remains available beneath it, and pressing back to the chooser
  // still works because this fires once per mount rather than on every render. Inert for every arrival
  // without the parameter, which keeps it out of the sign-up experiment's way.
  const openedForKnownEmail = useRef(false);
  useLayoutEffect(() => {
    if (openedForKnownEmail.current) {
      return;
    }
    openedForKnownEmail.current = true;
    if (!prefilledEmail(searchParams)) {
      return;
    }

    // Carry through the choice they already made. The checkbox lives on the chooser we are skipping,
    // and the store defaults this to true, so without this a reader who declined marketing on the
    // Labs payoff would be opted in here without ever seeing a box.
    const choice = prefilledNewsletter(searchParams);
    onChangeFlow({
      ...(choice === null ? {} : {
        newsletter: choice
      }),
      ...(isFormVisible ? {} : {
        isStartFormVisible: true
      })
    });
  }, [searchParams, isFormVisible, onChangeFlow]);
  useEffect(() => {
    const subscription = form.watch(values => {
      onChangeFlow({
        startFormDraft: {
          firstName: values.firstName ?? '',
          lastName: values.lastName ?? '',
          emailAddress: values.emailAddress ?? '',
          password: values.password ?? ''
        }
      });
    });
    return () => subscription.unsubscribe();
  }, [form, onChangeFlow]);
  useEffect(() => {
    // One screen means there is no second screen to open, so it reports itself under its own name rather
    // than claiming either of the two-screen states.
    const currentScreen = signUpScreenName(isOneScreen, isFormVisible);
    if (lastTrackedScreenRef.current === currentScreen) {
      return;
    }
    lastTrackedScreenRef.current = currentScreen;
    trackAuthScreenViewed('sign_up', currentScreen, 'start');
  }, [isOneScreen, isFormVisible, trackAuthScreenViewed]);
  const googleButton = <AuthGoogleButton isLoading={isGoogleLoading} label={isOneScreen ? 'Continue with Google' : 'Sign up with Google'} onClick={() => {
    trackSignUpChoiceClicked('google', newsletter);
    onSignUpWithGoogle(newsletter).catch(() => undefined);
  }} />;
  const orSeparator = (label: string) => <div className="relative flex w-full items-center justify-center p-3">
      <Separator />
      <span className="absolute mx-auto bg-white px-4 text-center text-gray-dark text-sm">{label}</span>
    </div>;
  const newsletterCheckbox = <Checkbox checked={newsletter} label="I want exclusive invites and opportunities at Extern" onCheckedChange={checked => onChangeFlow({
    newsletter: checked === true
  })} />;
  const termsText = <Text className="text-center" label="md">
      By signing up, you agree to Extern&apos;s{' '}
      <Link className="font-medium underline" href="https://www.extern.com/terms-of-service" target="_blank">
        Terms
      </Link>{' '}
      and{' '}
      <Link className="font-medium underline" href="https://www.extern.com/privacy-policy" target="_blank">
        Privacy Policy.
      </Link>
    </Text>;
  return <div className="flex w-full flex-col gap-8">
      <Form {...form}>
        <form className="flex w-full max-w-full flex-col items-center gap-6 overflow-visible rounded-3xl border border-role-outline-subtle bg-white p-6 sm:w-[358px]" onSubmit={form.handleSubmit(values_0 => {
        trackSignUpChoiceClicked('email', newsletter);
        onSignUpSubmit({
          firstName: values_0.firstName,
          lastName: values_0.lastName,
          emailAddress: values_0.emailAddress,
          ...(requiresPassword ? {
            password: values_0.password
          } : {}),
          newsletter
        });
      })}>
          {isOneScreen || isFormVisible ? <div className="flex w-full flex-col gap-4">
              {/* 🔴 THE CHOOSER IS THE LEAK. Two thirds of the people who ever see this page never click
                  either button on it: nothing to type, nothing to react to, just a decision. On this arm
                  Google keeps the top slot (three of four sign-ups use it) and the email fields sit
                  directly underneath, so the first thing a visitor sees is the thing they do. */}
              {isOneScreen ? <>
                  {googleButton}
                  {orSeparator('or use your email')}
                </> : null}
              {/* Hidden only once the portfolio's own name is in the form (and valid), never before. */}
              {portfolio.namesKnown ? null : <>
                  <FormField control={form.control} name="firstName" render={({
              field
            }) => <FormItem>
                        <FormLabel>First name</FormLabel>
                        <FormControl>
                          <Input placeholder="E.g. John" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  <FormField control={form.control} name="lastName" render={({
              field: field_0
            }) => <FormItem>
                        <FormLabel>Last name</FormLabel>
                        <FormControl>
                          <Input placeholder="E.g. Doe" {...field_0} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                </>}
              <FormField control={form.control} name="emailAddress" render={({
            field: field_1
          }) => <FormItem>
                    <FormLabel {...portfolio.lockedEmail ? PORTFOLIO_LOCKED_EMAIL_LABEL_PROPS : {}}>
                      {portfolio.lockedEmail ? 'Email' : 'Your email (personal, work or edu)'}
                      {portfolio.lockedEmail ? <PortfolioLockedEmailHint /> : null}
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="email@example.com" {...field_1} {...portfolio.lockedEmail ? PORTFOLIO_LOCKED_EMAIL_INPUT_PROPS : {}} />
                    </FormControl>
                    {requiresPassword ? null : <FormDescription>
                        We&apos;ll email you a 6-digit code to sign in.{' '}
                        <span className="font-medium text-role-on-background">No password needed.</span>
                      </FormDescription>}
                    <FormMessage />
                  </FormItem>} />

              {requiresPassword ? <FormField control={form.control} name="password" render={({
            field: field_2
          }) => <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        Create a password
                        <InputPasswordTooltip value={field_2.value} />
                      </FormLabel>
                      <FormControl>
                        <InputPassword autoCapitalize="none" autoComplete="password" autoCorrect="off" data-cy="password" id="password" key="password" placeholder="Type in your password" type="password" {...field_2} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} /> : null}

              {/* Side by side, the portfolio label ("Create account and see my matches") does not fit.
                  On one screen there is nowhere to return to, so the submit takes the whole row. */}
              <div className={portfolio.isPortfolioArrival || isOneScreen ? 'flex flex-col-reverse gap-2' : 'flex gap-2'}>
                {isOneScreen ? null : <Button appearance="outlined" className={portfolio.isPortfolioArrival ? 'w-full shrink-0' : 'flex-1'} color="info" disabled={isPending} onClick={() => {
              trackSignUpEmailReturnClicked('start_email_form', 'start');
              onChangeFlow({
                isStartFormVisible: false
              });
            }} type="button">
                    Return
                  </Button>}
                <Button appearance="filled" className={portfolio.isPortfolioArrival || isOneScreen ? 'w-full shrink-0' : 'flex-1'} color="info" disabled={isPending || !canSubmit} isLoading={isPending} type="submit">
                  {/* Portfolio arrivals are inside the passwordless test too (segmented, not excluded),
                      so on that arm the button says what happens next, same as for everyone else. */}
                  {submitLabel(requiresPassword, portfolio.isPortfolioArrival)}
                </Button>
              </div>

              {isPasswordless ? <Button appearance="ghost" className="mx-auto h-auto w-auto px-0 py-1 font-normal text-sm underline underline-offset-4" color="info" disabled={isPending} onClick={() => {
            trackSignUpPasswordOptionToggled(wantsPassword ? 'email_code' : 'password');
            if (wantsPassword) {
              // A hidden value must never be submitted, and a hidden error must not keep the
              // button disabled.
              form.setValue('password', '');
              form.clearErrors('password');
            }
            setWantsPassword(!wantsPassword);
          }} type="button">
                  {wantsPassword ? 'Email me a code instead' : 'Create a password instead'}
                </Button> : null}

              {isOneScreen ? <>
                  {newsletterCheckbox}
                  <Separator />
                  {termsText}
                </> : null}
            </div> : <>
              {googleButton}

              {orSeparator('or')}

              <AuthLastUsedButtonWrap matchStrategy={EMAIL_AUTHENTICATION_STRATEGIES} strategy={lastAuthenticationStrategy}>
                <Button appearance="outlined" className="w-full" color="info" onClick={() => {
              trackSignUpChoiceClicked('email', newsletter);
              onChangeFlow({
                isStartFormVisible: true
              });
            }} type="button">
                  Sign up with Email
                </Button>
              </AuthLastUsedButtonWrap>

              {newsletterCheckbox}

              <Separator />

              {termsText}
            </>}
        </form>
      </Form>
      {isFormVisible && !isOneScreen ? null : <LinkButton appearance="ghost" className="gap-0" color="neutral" href={`/sign-in?${getAllowlistedSearchParams(searchParams)}`}>
          Already have an account?&nbsp;<span className="text-role-info">Sign in</span>
        </LinkButton>}
    </div>;
}